<template>
	<v-container class="space-y-lg pb-xl md:space-y-2xl">
		<div class="space-y-md">
			<v-h3 v-if="story.content.title" tag="h1" class="mb-5">
				{{ story.content.title }}
			</v-h3>

			<v-storyblok-component
				:is="story.content.active_campaigns[0]"
				v-if="hasActiveCampaigns"
				:story="story.content.active_campaigns"
				:blok="story.content.active_campaigns[0]"
			/>

			<div class="flex w-full flex-col gap-8 md:flex-row md:items-start">
				<nuxt-picture
					v-if="story.content.image?.filename"
					:src="story.content.image.filename"
					:alt="story.content.image?.alt ?? story.content.title"
					provider="storyblok"
					loading="lazy"
					v-bind="sbImageDimensions(story.content.image.filename)"
					sizes="xs:100vw sm:640px md:768px lg:325px xl:410px 2xl:496px"
					class="block w-full md:w-1/2"
					:img-attrs="{
						// Max-h is a sanity to keep tall+narrow images from taking tons of vertical space
						class: 'size-full max-h-[400px] object-contain rounded',
					}"
				/>

				<sb-rich-text
					v-if="story.content.description"
					:document="story.content.description"
					class="prose-5xs w-full text-(--font-color) md:w-1/2"
					:style="{
						'--font-color': story.content.description_color?.color,
					}"
				/>
			</div>
		</div>

		<v-scrollable
			v-if="subCategories.length > 0"
			class="flex snap-x scroll-px-sm flex-nowrap space-x-5 px-sm sm:px-0 md:scroll-px-0"
		>
			<v-scrollable-item
				v-for="category in subCategories"
				:key="category.id"
				class="w-40 shrink-0 snap-start"
			>
				<nuxt-link
					:to="`/${category.full_slug}`"
					:aria-label="category.name"
					class="flex flex-col space-y-xs hover:underline"
				>
					<div class="size-[160px] overflow-hidden rounded-full bg-brand-gray">
						<nuxt-picture
							v-if="
								category.content.menu_image?.filename &&
								category.content.menu_image?.filename.length > 0
							"
							:src="category.content.menu_image.filename"
							provider="storyblok"
							loading="lazy"
							height="110"
							width="110"
							fit="in"
							:alt="category.content.meta.title"
							:aria-label="t('aria.navigate', {page: category.name})"
							sizes="xs:110px sm:110px md:110px lg:110px xl:110px 2xl:110px"
							:img-attrs="{
								class:
									'object-contain m-[25px] w-[110px] h-[110px] mix-blend-multiply',
							}"
						/>

						<nuxt-picture
							v-else-if="
								category.content.image?.filename &&
								category.content.image?.filename.length > 0
							"
							:src="category.content.image.filename"
							provider="storyblok"
							loading="lazy"
							height="160"
							width="160"
							:alt="category.content.meta.title"
							:aria-label="t('aria.navigate', {page: category.name})"
							sizes="xs:160px sm:160px md:160px lg:160px xl:160px 2xl:160px"
							:img-attrs="{
								class: 'object-cover w-[160px] h-[160px] mix-blend-multiply',
							}"
						/>
					</div>

					<span class="flex justify-center text-center">
						{{ category.name }}
					</span>
				</nuxt-link>
			</v-scrollable-item>
		</v-scrollable>

		<template
			v-for="component in story.content.additional_bloks"
			:key="component._uid"
		>
			<v-storyblok-component :is="component" :blok="component" />
		</template>

		<v-elevate-product-list
			v-slot="{
				products,
				productCount: listProductCount,
				hasNextPage,
				loadMore,
				getAdForIndex,
				showCtaForIndex,
				activeFiltersCount,
				clearFilter,
			}"
			:campaign-only="isCampaignOnly"
			load-ads
			class="space-y-lg px-sm sm:px-0"
		>
			<div class="flex flex-col justify-between">
				<p class="text-sm tracking-xl text-gray-500 md:block">
					{{ listProductCount }} {{ t("amount-products") }}
				</p>

				<div class="mt-sm flex gap-lg lg:pt-md">
					<v-button
						type="button"
						theme="black"
						size="sm"
						class="flex items-center space-x-xs hover:underline"
						@click="isModalOpen = true"
					>
						<template v-if="activeFiltersCount > 0">
							<v-category-filter-count
								:active-filters-count="activeFiltersCount"
							/>
						</template>

						<span>{{ t("filter-title") }}</span>

						<v-icon icon="filter" class="text-lg"></v-icon>
					</v-button>

					<v-button
						v-if="activeFiltersCount > 0"
						theme="gray"
						size="sm"
						@click="clearFilter"
					>
						{{ t("filter-clear") }}
					</v-button>
				</div>

				<v-elevate-common-facets class="mt-xs" />

				<div class="mt-md flex items-center justify-between gap-sm">
					<v-category-order />

					<v-category-preferred-image-type-toggle />
				</div>
			</div>

			<div
				class="grid grid-cols-2 gap-x-xs gap-y-lg md:grid-cols-3 md:gap-x-sm md:gap-y-xl lg:grid-cols-4"
			>
				<template v-for="(product, index) in products" :key="product.key">
					<v-category-cta
						v-if="ctaContent != null && isCtaVisible && showCtaForIndex(index)"
						:content="ctaContent"
						class="col-span-full"
					/>

					<!-- Ads -->
					<v-storyblok-listing-ad
						v-if="getAdForIndex(index)"
						:ad="getAdForIndex(index)"
					/>

					<v-flowbox
						v-if="story.content.flowbox_key && index === 8"
						:flowbox-key="story.content.flowbox_key"
						:tags="flowboxTags"
						class="col-span-full"
					/>

					<v-elevate-listing-product-card :product="product" />
				</template>
			</div>

			<div class="grid md:grid-cols-8">
				<div class="space-y-md md:col-span-2 md:col-start-4">
					<client-only>
						<v-button
							class="w-full"
							:theme="hasNextPage ? 'black' : 'gray'"
							:disabled="!hasNextPage"
							:aria-label="
								hasNextPage
									? $t('loader.show-more')
									: $t('loader.show-more-loaded')
							"
							@click="loadMore"
						>
							{{
								hasNextPage
									? $t("loader.show-more")
									: $t("loader.show-more-loaded")
							}}
						</v-button>
					</client-only>

					<div class="space-y-xs">
						<div class="h-[2px] rounded-full bg-gray-300">
							<div
								class="h-[2px] rounded-full bg-black"
								:style="{
									width: `${(products.length / listProductCount) * 100}%`,
								}"
							></div>
						</div>

						<p class="text-center">
							{{ $t("loader.show-more-prefix") }} {{ products.length }} /
							{{ listProductCount }} {{ $t("loader.show-more-suffix") }}
						</p>
					</div>

					<button
						class="w-full text-base underline"
						type="button"
						@click="scrollToTop"
					>
						<span class="">{{ $t("loader.back-to-start") }}</span>
					</button>
				</div>
			</div>

			<v-category-filter-modal
				:is-open="isModalOpen"
				:results="listProductCount"
				@close="isModalOpen = false"
			/>
		</v-elevate-product-list>

		<div
			v-if="story.content.content"
			class="space-y-sm wrap-break-word text-gray-500"
		>
			<div>
				<sb-rich-text :document="story.content.content" class="prose-5xs" />
			</div>
		</div>
	</v-container>
</template>

<script setup lang="ts">
import type {ISbStoryData} from "storyblok-js-client";
import type {SbRichTextDoc as ISbRichtext} from "@storyblok/js";
import {useDefaultDynamicHead} from "@pkg/shared/composables/use-default-dynamic-head";
import {useElevateProductListState} from "@pkg/shared/composables/use-elevate-product-list-state";
import {useStoryblokStoriesList} from "@pkg/shared/composables/use-storyblok-stories-list";
import {useStoryblokStory} from "@pkg/shared/composables/use-storyblok-story";
import sbImageDimensions from "@pkg/shared/utils/sb-image-dimensions";
import type {
	BlokAsset,
	MetaFieldsPlugin,
	CmdProductListPlugin,
	BlokColorpicker,
	ListingAdComponent,
	BlokComponent,
} from "@pkg/shared/types";

const properties = defineProps<{
	story: ISbStoryData<ClubLander>;
}>();

interface ClubLander {
	component: "sb-club-lander";
	meta: MetaFieldsPlugin;
	image: BlokAsset;
	title: string;

	category_menu: string[];
	content: ISbRichtext;
	products: CmdProductListPlugin;

	campaign_only: boolean;
	description: ISbRichtext;

	short_description: string;

	description_color: BlokColorpicker;

	on_pages: string[];
	ads: ListingAdComponent[];

	active_campaigns: ISbStoryData[];

	og_image: BlokAsset;

	additional_bloks: BlokComponent[];

	flowbox_key: string | undefined;

	flowbox_tags: string | undefined;
}

const {t} = useI18n();

const categoryLevel = computed(
	() => properties.story.full_slug.replace(/\/$/, "").split("/").length,
);

const {data: ctaContent} = useStoryblokStory("global/category-cta");

const categoryId = computed(() => properties.story.uuid);

const isCtaVisible = computed(
	() =>
		ctaContent.value?.content.on_pages?.length &&
		ctaContent.value?.content.on_pages.includes(categoryId.value),
);

const isModalOpen = ref(false);
const isCampaignOnly = computed(() => properties.story.content.campaign_only);

const flowboxTags = computed(() =>
	properties.story.content.flowbox_tags
		? properties.story.content.flowbox_tags.split(" ").map((tag) => tag.trim())
		: undefined,
);

const scrollToTop = () => {
	window.scrollTo({top: 0, behavior: "smooth"});
};

const {
	data: {value: subCategoriesData},
} = await useStoryblokStoriesList(
	`subcategories-${properties.story.full_slug}`,
	{
		starts_with: properties.story.full_slug,
		excluding_ids: String(properties.story.id),
		per_page: 50,
	},
);

const subCategories = subCategoriesData
	.filter(
		(story) =>
			story.full_slug.replace(/\/$/, "").split("/").length ===
			categoryLevel.value + 1,
	)
	.sort((a, b) => a.position - b.position);

const hasActiveCampaigns = computed(
	() => properties.story.content.active_campaigns?.length > 0,
);

useSeoMeta({
	title: properties.story.content.meta?.title || properties.story.name,

	description:
		(properties.story.content.meta?.description ||
			properties.story.content?.short_description) ??
		undefined,
	ogTitle:
		properties.story.content.meta?.title ||
		properties.story.content.title ||
		properties.story.name,

	ogDescription:
		(properties.story.content.meta?.description ||
			properties.story.content?.short_description) ??
		undefined,
	ogImage: properties.story.content?.og_image?.filename ?? undefined,
});

useDefaultDynamicHead({includeQueryInCanonicalUrl: false});

// CollectionPage structured data
const {appUrl} = useRuntimeConfig().public;

const {productCount} = useElevateProductListState({});

useJsonld(() => ({
	"@context": "https://schema.org",
	"@type": "CollectionPage",
	name:
		properties.story.content.meta?.title ||
		properties.story.content.title ||
		properties.story.name,
	description:
		(properties.story.content.meta?.description ||
			properties.story.content?.short_description) ??
		undefined,
	url: new URL(properties.story.full_slug, appUrl).href,
	mainEntity: {
		"@type": "ItemList",
		name:
			properties.story.content.meta?.title ||
			properties.story.content.title ||
			properties.story.name,
		numberOfItems:
			productCount.value && productCount.value > 0
				? productCount.value
				: undefined,
	},
}));
</script>

<i18n lang="json">
{
	"et": {
		"amount-products": "toodet",
		"filter-title": "Filtrid",
		"read-more": "Loe rohkem",
		"show-less": "Näita vähem"
	},
	"fi": {
		"amount-products": "tuotetta",
		"filter-title": "Suodata",
		"read-more": "Lue lisää",
		"show-less": "Näytä vähemmän"
	}
}
</i18n>
